import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Ticket, AlertTriangle, Clock, CheckCircle2, ArrowRight } from 'lucide-react';

/**
 * Compact role-scoped helpdesk widget for /dashboard.
 * - Employee: shows "My open" + "Resolved (all-time)".
 * - HR / IT / Payroll / Admin: shows queue KPIs (open / critical / SLA breaches).
 * Auto-hides when summary endpoint can't be loaded.
 */
export default function MyTicketsWidget() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const isStaff = ['admin','hr','it_admin','payroll_manager'].some(r => (user?.roles || []).includes(r) || user?.role === r);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/tickets/summary');
        if (!cancelled) setS(data);
      } catch (e) { if (!cancelled) setS(null); }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  if (!s) return null;
  // For pure employees with zero activity, keep the dashboard clean
  if (!isStaff && s.open === 0 && s.resolved === 0 && s.closed === 0) return null;

  const cards = isStaff
    ? [
        { label: 'Open in my queue', value: s.open, Icon: Ticket, accent: 'text-indigo-600 bg-indigo-50' },
        { label: 'Critical', value: s.critical_open, Icon: AlertTriangle, accent: 'text-rose-600 bg-rose-50' },
        { label: 'SLA breached', value: s.breached_open, Icon: Clock, accent: 'text-amber-600 bg-amber-50' },
        { label: 'Resolved today', value: s.resolved_today, Icon: CheckCircle2, accent: 'text-emerald-600 bg-emerald-50' },
      ]
    : [
        { label: 'My open tickets', value: s.open, Icon: Ticket, accent: 'text-indigo-600 bg-indigo-50' },
        { label: 'Resolved', value: s.resolved, Icon: CheckCircle2, accent: 'text-emerald-600 bg-emerald-50' },
        { label: 'Closed', value: s.closed, Icon: CheckCircle2, accent: 'text-slate-600 bg-slate-100' },
      ];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="my-tickets-widget">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Helpdesk</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">
            {isStaff ? 'Tickets in your queue' : 'Your tickets'}
          </h2>
        </div>
        <Link to="/tickets" className="text-xs font-semibold text-[#3D4A8E] hover:underline inline-flex items-center gap-1" data-testid="tickets-view-all">
          Open helpdesk <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className={`grid gap-3 mt-4 ${cards.length === 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'}`}>
        {cards.map(c => (
          <div key={c.label} className="border border-slate-200 rounded-xl p-3 flex items-start justify-between" data-testid={`tw-kpi-${c.label.toLowerCase().replace(/\s/g,'-')}`}>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{c.label}</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{c.value}</div>
            </div>
            <div className={`w-8 h-8 rounded-lg ${c.accent} flex items-center justify-center`}>
              <c.Icon className="w-4 h-4" strokeWidth={1.8} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
